import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Spinner, Alert, Form } from 'react-bootstrap';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import './AdminBillingReport.css';

const AdminBillingReport = ({ ViewedPage, app_url }) => {
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, with_subscription, without_subscription

  useEffect(() => {
    ViewedPage(106);
    fetchBillingReport();
  }, [ViewedPage]);

  const fetchBillingReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${app_url}/api/subscriptions/billing-report`, {
        withCredentials: true
      });
      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error fetching billing report:', error);
      toast.error('Failed to load billing report');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: 'success', text: 'Active' },
      pending: { variant: 'warning', text: 'Pending' },
      cancelled: { variant: 'danger', text: 'Cancelled' },
      paused: { variant: 'secondary', text: 'Paused' },
      created: { variant: 'info', text: 'Created' },
      authenticated: { variant: 'primary', text: 'Authenticated' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'INR' ? '₹' : '$';
    return `${symbol}${amount.toLocaleString()}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredData = reportData.filter(item => {
    if (filter === 'with_subscription') return item.has_subscription;
    if (filter === 'without_subscription') return !item.has_subscription;
    return true;
  });

  return (
    <div className="main-container admin-billing-report-container">
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="mb-0">Shop Billing Report</h2>
            <p className="text-muted">View all shops and their current subscription plans</p>
          </Col>
        </Row>

        {/* Summary Cards */}
        {summary && (
          <Row className="mb-4 g-3">
            <Col xs={12} sm={6} md={4}>
              <Card className="stat-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Total Shops</h6>
                  <h3 className="mb-0">{summary.total_shops}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Card className="stat-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Shops with Subscription</h6>
                  <h3 className="mb-0 text-success">{summary.shops_with_subscription}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Card className="stat-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Shops without Subscription</h6>
                  <h3 className="mb-0 text-warning">{summary.shops_without_subscription}</h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Filter */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="g-3">
              <Col xs={12} md={6} lg={4}>
                <Form.Label>Filter</Form.Label>
                <Form.Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All Shops</option>
                  <option value="with_subscription">With Subscription</option>
                  <option value="without_subscription">Without Subscription</option>
                </Form.Select>
              </Col>
              <Col xs={12} md={6} lg={4} className="d-flex align-items-end">
                <Button variant="primary" onClick={fetchBillingReport} className="w-100 w-md-auto">
                  Refresh
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Billing Report Table */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">Shop Billing Details</h5>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : filteredData.length === 0 ? (
              <Alert variant="info">No shops found</Alert>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Shop</th>
                      <th>Shop Name</th>
                      <th>Has Subscription</th>
                      <th>Plan Name</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>User Email</th>
                      <th>Razorpay ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item, index) => (
                      <tr key={index}>
                        <td><code>{item.shop}</code></td>
                        <td>{item.shop_name}</td>
                        <td>
                          {item.has_subscription ? (
                            <Badge bg="success">Yes</Badge>
                          ) : (
                            <Badge bg="secondary">No</Badge>
                          )}
                        </td>
                        <td>
                          {item.subscription ? (
                            item.subscription.plan_name
                          ) : (
                            <span className="text-muted">No Plan</span>
                          )}
                        </td>
                        <td>
                          {item.subscription ? (
                            `${formatCurrency(item.subscription.plan_price, item.subscription.plan_currency)} / ${item.subscription.plan_interval}`
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {item.subscription ? (
                            getStatusBadge(item.subscription.status)
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {item.subscription ? formatDate(item.subscription.current_start) : '-'}
                        </td>
                        <td>
                          {item.subscription ? formatDate(item.subscription.current_end) : '-'}
                        </td>
                        <td>
                          {item.subscription ? item.subscription.user_email : '-'}
                        </td>
                        <td>
                          {item.subscription?.razorpay_subscription_id ? (
                            <code className="small">{item.subscription.razorpay_subscription_id}</code>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AdminBillingReport;
